import React, { useEffect, useState } from "react";
import { FaBell, FaFolder, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { showToast } from "../components/Toaster";
import API_BASE_URL from '../config/api';

export default function UserDashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setProfile(JSON.parse(userData));
    } else {
      navigate("/userlogin"); // Redirect if not logged in
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/userlogin");
  };

  const [activeSection, setActiveSection] = useState<'profile' | 'documents' | 'notifications'>('profile');
  // Lazy load Documents page
  const Documents = React.lazy(() => import('./Documents'));

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPin, setShowPin] = useState(false);

  const [passwordLoading, setPasswordLoading] = useState(false);
  const handlePasswordChange = async () => {
    setPasswordError("");
    if (!oldPassword) {
      setPasswordError("Please enter your current password.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: profile?.id,
          oldPassword,
          newPassword
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowPasswordModal(false);
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        showToast("Password changed successfully!", "success");
      } else {
        setPasswordError(data.message || "Failed to change password.");
      }
    } catch (err) {
      setPasswordError("Server error. Please try again.");
    }
    setPasswordLoading(false);
  };

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    localStorage.removeItem("user");
    navigate("/userlogin");
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <div className="flex h-screen">
      {/* Side Menu */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col shadow-lg">
        {/* Logo at top */}
        <div className="flex flex-col items-center justify-center py-8">
          <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" className="h-auto w-[90%] mb-2 shadow" />
          {/* <span className="font-bold text-xl tracking-wide">HT Immigration</span>          */}
        </div>
        <div className="bg-blue-900 px-6 py-4 border-b border-gray-800">
           <span className="font-bold text-xl tracking-wide ">User Dashboard</span>
        </div>
        {/* Navigation */}
        <nav className="flex-1 px-4 py-8">
          <ul className="space-y-2">
            <li
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-semibold cursor-pointer transition-colors ${activeSection === 'profile' ? 'bg-blue-600 text-white' : 'hover:bg-gray-800 hover:text-blue-400 text-gray-200'}`}
              onClick={() => setActiveSection('profile')}
            >
              <span className="material-icons"><FaUser /></span> Profile
            </li>
            <li
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-semibold cursor-pointer transition-colors ${activeSection === 'documents' ? 'bg-blue-600 text-white' : 'hover:bg-gray-800 hover:text-blue-400 text-gray-200'}`}
              onClick={() => setActiveSection('documents')}
            >
              <span className="material-icons"><FaFolder/></span> Documents
            </li>
            <li
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-semibold cursor-pointer transition-colors ${activeSection === 'notifications' ? 'bg-blue-600 text-white' : 'hover:bg-gray-800 hover:text-blue-400 text-gray-200'}`}
              onClick={() => setActiveSection('notifications')}
            >
              <span className="material-icons"><FaBell /></span> Notifications
            </li>
          </ul>
        </nav>
        <button
          className="m-4 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded font-semibold"
          onClick={handleLogoutClick}
        >
          Logout
        </button>
      </aside>
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="flex items-center justify-end bg-white shadow p-4">
          {/* <div className="font-bold text-lg">User Dashboard</div> */}
          <div className="flex items-center gap-4">
            <span>{profile?.first_name} {profile?.last_name}</span>
            {/* <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded">{profile?.role}</span> */}
            <button
              className="bg-gray-900 text-white px-3 py-1 rounded"
              onClick={handleLogoutClick} // <-- use confirm popup
            >
              Logout
            </button>
          </div>
        </header>
        {/* Content */}
        <main className="flex-1 p-0 bg-gray-50">
          {activeSection === 'profile' && (
            <section className="mt-12 flex justify-center">
              <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
                <h2 className="text-2xl font-bold mb-6 text-center text-[#0097b2]">Profile</h2>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Username:</span>
                    <span>{profile?.username}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Name:</span>
                    <span>{profile?.first_name} {profile?.last_name}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Email:</span>
                    <span>{profile?.email}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Role:</span>
                    <span className="px-2 py-1 rounded bg-blue-100 text-blue-800">{profile?.role}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">PIN:</span>
                    <span>
                      {showPin ? profile?.pin : "***"}
                      <button
                        className="ml-2 text-blue-600 underline text-sm"
                        onClick={() => setShowPin((prev) => !prev)}
                        type="button"
                      >
                        {showPin ? "Hide" : "Show"}
                      </button>
                    </span>
                  </div>
                </div>
                <button
                  className="mt-8 w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 font-semibold transition"
                  onClick={() => setShowPasswordModal(true)}
                >
                  Change Password
                </button>
              </div>
              {/* Change Password Modal */}
              {showPasswordModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm relative">
                    <button
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl font-bold"
                      onClick={() => setShowPasswordModal(false)}
                    >
                      &times;
                    </button>
                    <h3 className="text-xl font-bold mb-4 text-center">Change Password</h3>
                    <div className="mb-4">
                      <label className="block font-semibold mb-1">Current Password</label>
                      <input
                        type="password"
                        className="border p-2 rounded w-full"
                        value={oldPassword}
                        onChange={e => setOldPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block font-semibold mb-1">New Password</label>
                      <input
                        type="password"
                        className="border p-2 rounded w-full"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        minLength={6}
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block font-semibold mb-1">Confirm Password</label>
                      <input
                        type="password"
                        className="border p-2 rounded w-full"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        minLength={6}
                        required
                      />
                    </div>
                    {passwordError && (
                      <div className="text-red-600 mb-2">{passwordError}</div>
                    )}
                    <button
                      className={`bg-blue-600 text-white px-4 py-2 rounded w-full font-semibold ${passwordLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={handlePasswordChange}
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? 'Saving...' : 'Save Password'}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
          {activeSection === 'documents' && (
            <React.Suspense fallback={<div>Loading Documents...</div>}>
              <Documents />
            </React.Suspense>
          )}
          {activeSection === 'notifications' && (
            <section>
              <h2 className="text-xl font-bold mb-2">Notifications</h2>
              <div>This section will show user notifications.</div>
            </section>
          )}
        </main>
      </div>
      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm relative">
            <h3 className="text-xl font-bold mb-4 text-center">Confirm Logout</h3>
            <p className="mb-6 text-center">Are you sure you want to logout?</p>
            <div className="flex justify-center gap-4">
              <button
                className="bg-red-600 text-white px-4 py-2 rounded font-semibold"
                onClick={confirmLogout}
              >
                Yes, Logout
              </button>
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded font-semibold"
                onClick={cancelLogout}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}